import React, { useState, useEffect } from 'react';
import { Sparkles, Droplet, Circle, Wind, ArmchairIcon, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  sparkles: Sparkles,
  droplet: Droplet,
  circle: Circle,
  wind: Wind,
  armchair: ArmchairIcon,
  car: Droplet,
  sparkle: Sparkles
};

export const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch(`${API}/admin/services`);
        const data = await res.json();
        const servicesArray = Array.isArray(data) ? data : [];
        setServices(servicesArray.sort((a, b) => (a.order || 0) - (b.order || 0)));
      } catch (err) {
        console.error('Error fetching services:', err);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="services" className="py-12 sm:py-20 md:py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-12 sm:py-20 md:py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header - More Modern */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <div className="inline-block mb-3 sm:mb-4">
            <span className="px-4 sm:px-6 py-1.5 sm:py-2 rounded-full bg-blue-100 text-blue-600 font-semibold text-xs sm:text-sm tracking-wider uppercase">
              Naše služby
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-4 sm:mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent px-4">
            Kompletní péče o váš vůz
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
            Nabízím profesionální služby s důrazem na kvalitu a detail
          </p>
        </div>

        {/* Services Grid - Enhanced Cards with Fixed Button Position */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Sparkles;
            const hasImage = service.image && typeof service.image === 'string' && service.image.startsWith('http');
            return (
              <Card
                key={service.id}
                className="group relative hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-0 bg-white/80 backdrop-blur-sm overflow-hidden flex flex-col"
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                {/* Gradient Overlay on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/5 group-hover:to-cyan-500/5 transition-all duration-500 pointer-events-none"></div>
                
                {/* Service Image/Icon */}
                <div className="relative h-56 overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50">
                  {hasImage ? (
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  ) : IconComponent ? (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-cyan-100">
                      <IconComponent size={80} className="text-blue-600" strokeWidth={1.5} />
                    </div>
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  
                  {/* Icon Badge - Glassmorphism */}
                  {IconComponent && (
                    <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-xl text-white p-4 rounded-2xl shadow-xl border border-white/30 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent size={28} strokeWidth={2.5} />
                    </div>
                  )}
                  
                  {/* Price Badge */}
                  <div className="absolute bottom-4 left-4 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold text-lg shadow-xl">
                    {service.price}
                  </div>
                </div>

                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-black text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                    {service.name}
                  </CardTitle>
                </CardHeader>

                <CardContent className="pb-6 flex-grow">
                  <ul className="space-y-3">
                    {service.description && Array.isArray(service.description) && service.description.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-700">
                        <div className="mt-0.5 flex-shrink-0">
                          <Check size={20} className="text-blue-600" strokeWidth={3} />
                        </div>
                        <span className="text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="pt-0 mt-auto">
                  <Button
                    onClick={scrollToContact}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white font-bold py-6 rounded-xl transition-all duration-300 transform group-hover:scale-105 shadow-lg group-hover:shadow-xl"
                  >
                    Objednat
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Additional Info - Enhanced - MOBILE FIXED */}
        <div className="text-center bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl sm:rounded-3xl p-6 sm:p-12 shadow-xl">
          <p className="text-base sm:text-xl text-gray-700 mb-4 sm:mb-6 font-medium px-2">
            Máte speciální požadavky nebo potřebujete kombinaci služeb?
          </p>
          <Button
            size="lg"
            onClick={scrollToContact}
            variant="outline"
            className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-bold px-6 sm:px-10 py-4 sm:py-6 rounded-xl transition-all duration-300 transform hover:scale-105 text-sm sm:text-base w-full sm:w-auto"
          >
            Kontaktujte mě pro individuální nabídku
          </Button>
        </div>
      </div>
    </section>
  );
};
